import { useEffect } from 'react';

const OVERLAY_SELECTOR = '[data-rt-modal-overlay]';

const CLOSE_BUTTON_SELECTOR = [
  '[data-rt-modal-close]',
  'button.close-btn',
  'button.modal-close',
  'button[aria-label="Close"]',
  'button[aria-label="close"]',
  '.rt-link-suites-close',
  '.rt-plan-modal-close',
].join(', ');

const shouldIgnoreEscapeTarget = (target) => {
  if (!target || !(target instanceof Element)) return false;
  if (target.closest('[data-rt-modal-keep-escape]')) return true;
  return false;
};

const dismissOverlay = (overlay) => {
  if (!overlay) return;
  // Prefer explicit overlay onClick (outside-click handler)
  overlay.dispatchEvent(
    new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
  );
  // If still visible (no handler / stopPropagation), click a close control
  if (document.body.contains(overlay) && overlay.matches(OVERLAY_SELECTOR)) {
    const closeBtn = overlay.querySelector(CLOSE_BUTTON_SELECTOR);
    if (closeBtn) closeBtn.click();
  }
};

/**
 * Global Escape → dismiss the topmost open modal overlay.
 * Overlay nodes should set data-rt-modal-overlay.
 * Outside-click: set onClick={onClose} on the overlay (stopPropagation on the panel).
 */
export function installModalEscapeDismiss() {
  const onKeyDown = (event) => {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (event.defaultPrevented) return;
    if (shouldIgnoreEscapeTarget(event.target)) return;

    const overlays = document.querySelectorAll(OVERLAY_SELECTOR);
    if (!overlays.length) return;

    const top = overlays[overlays.length - 1];
    event.preventDefault();
    event.stopPropagation();
    dismissOverlay(top);
  };

  document.addEventListener('keydown', onKeyDown, true);
  return () => document.removeEventListener('keydown', onKeyDown, true);
}

/**
 * Attach outside-click dismiss to an overlay node that has no onClick yet.
 * Call from modal render refs if needed; prefer explicit onClick={onClose}.
 */
export function useModalDismiss(open, onClose, { enabled = true } = {}) {
  useEffect(() => {
    if (!open || !enabled || typeof onClose !== 'function') return undefined;

    const onKeyDown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (event.defaultPrevented) return;
      if (shouldIgnoreEscapeTarget(event.target)) return;
      event.preventDefault();
      onClose();
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose, enabled]);
}

export default useModalDismiss;
